import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ReferralsPage.css';
import { useStore } from '../store/useStore';
import { formatNumber } from '../utils/formatters';
import api, { gameAPI } from '../services/api';
import SoundManager from '../services/SoundManager';
import ReferralEarningsChart from '../components/ReferralEarningsChart';
import ReferralTree from '../components/ReferralTree';

// Импортируем SVG иконки
import CheckCircleIcon from '../assets/icons/check-circle.svg';
import LockIcon from '../assets/icons/lock.svg';
import UsersIcon from '../assets/icons/users.svg';

// Требования для разблокировки уровней
const LEVEL_REQUIREMENTS = {
  1: { deposit: 0, referrals: 0 }, // Всегда открыт
  2: { deposit: 200, referrals: 1 },
  3: { deposit: 400, referrals: 2 },
  4: { deposit: 600, referrals: 3 },
  5: { deposit: 800, referrals: 4 }
};

const ReferralsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, spermPrice } = useStore();
  const isWebMode = localStorage.getItem('webauth') === 'true';
  const tgRefLink = `https://t.me/moonraid_game_bot?start=${user?.referralCode || ''}`;
  const webRefLink = `https://safepermoon.com/?ref=${user?.referralCode || ''}`;
  // В веб-версии показываем веб-ссылку, в ТГ — ТГ-ссылку
  const referralLink = isWebMode ? webRefLink : tgRefLink;
  const [hasEmail, setHasEmail] = useState(false);
  // Обновляем hasEmail при каждом фокусе на вкладке
  useEffect(() => {
    const checkEmail = async () => {
      try {
        const res = await api.get('/referrals/my');
        if (res.data.success && res.data.data?.hasEmail) setHasEmail(true);
      } catch (e) {}
    };
    const onVisible = () => { if (!document.hidden) checkEmail(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
  const [showLeaderModal, setShowLeaderModal] = useState(false);
  const [showRewardsModal, setShowRewardsModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<number>(2);
  const [referralStats, setReferralStats] = useState<any>(null);
  const [referralList, setReferralList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const [expandedLevels, setExpandedLevels] = useState<Set<number>>(new Set());
  const [levelReferrals, setLevelReferrals] = useState<Record<number, any[]>>({});
  const [userLunarDeposit, setUserLunarDeposit] = useState(0);
  const [activeReferralsCount, setActiveReferralsCount] = useState(0);
  const [showAsReferrer, setShowAsReferrer] = useState(true);
  const [loadingPrivacy, setLoadingPrivacy] = useState(false);

  // Определение лидерского статуса
  const getLeaderStatus = (referralCount: number) => {
    if (referralCount >= 100) return { level: 5, name: t('referrals.leaderModalDiamond'), bonus: 0.5, next: null };
    if (referralCount >= 50) return { level: 4, name: t('referrals.leaderModalPlatinum'), bonus: 0.3, next: 100 };
    if (referralCount >= 30) return { level: 3, name: t('referrals.leaderModalGold'), bonus: 0.2, next: 50 };
    if (referralCount >= 10) return { level: 2, name: t('referrals.leaderModalSilver'), bonus: 0.1, next: 30 };
    if (referralCount >= 3) return { level: 1, name: t('referrals.leaderModalBronze'), bonus: 0.05, next: 10 };
    return { level: 0, name: t('referrals.novice'), bonus: 0, next: 3 };
  };
  
  // Для лидерского статуса считаем ТОЛЬКО АКТИВНЫХ рефералов первой линии (с Lunar/Star стейкингом >= 25)
  const firstLineReferrals = referralStats?.levelsStats?.find((l: any) => l.level === 1)?.referralCount || 0;
  // Общее количество рефералов по всем линиям (для отображения)
  const totalReferrals = referralStats?.totalStats?.totalReferrals || user?.referralCount || 0;
  // Лидерский статус определяется по АКТИВНЫМ рефералам первой линии
  const leaderStatus = getLeaderStatus(activeReferralsCount);

  // Проверка разблокировки уровня - используем данные из API
  const isLevelUnlocked = (level: number): boolean => {
    // Уровень 1 всегда открыт
    if (level === 1) return true;

    // Используем isUnlocked из API вместо локального расчёта
    const levelData = referralStats?.levelsStats?.find((l: any) => l.level === level);
    if (levelData) {
      return levelData.isUnlocked === true;
    }

    // Fallback на локальную логику если API данные ещё не загружены
    const req = LEVEL_REQUIREMENTS[level as keyof typeof LEVEL_REQUIREMENTS];
    if (!req) return false;
    return userLunarDeposit >= req.deposit && activeReferralsCount >= req.referrals;
  };

  // Функция для склонения слова "реферал"
  const getReferralWord = (count: number) => {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    // Для русского языка используем склонение
    if (t('referrals.referralWord') === 'реферал') {
      if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        return t('referrals.referralWordPlural');
      }
      if (lastDigit === 1) {
        return t('referrals.referralWord');
      }
      if (lastDigit >= 2 && lastDigit <= 4) {
        return 'реферала';
      }
      return t('referrals.referralWordPlural');
    }

    // Для английского просто множественное/единственное число
    return count === 1 ? t('referrals.referralWord') : t('referrals.referralWordPlural');
  };
  
  // Загружаем статистику рефералов и данные для разблокировки уровней
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        // Получаем статистику рефералов (+ hasEmail)
        const refResponse = await api.get('/referrals/my');
        if (refResponse.data.success) {
          setReferralStats(refResponse.data.data);
          if (refResponse.data.data?.hasEmail) setHasEmail(true);
        }

        // Получаем данные о lunar + star депозитах пользователя (как на бэкенде в referralLevels.js)
        const stakingResponse = await api.get('/staking/my');
        if (stakingResponse.data.success) {
          const qualifyingDeposits = stakingResponse.data.data.filter((d: any) =>
            d.staking_type === 'lunar' || d.staking_type === 'star'
          );
          const totalQualifying = qualifyingDeposits.reduce((sum: number, d: any) => sum + parseFloat(d.amount || 0), 0);
          setUserLunarDeposit(totalQualifying);
        }

        // Получаем количество активных рефералов (с лунным стейкингом >= 100 SPERM)
        const activeRefsResponse = await api.get('/referrals/active-count');
        if (activeRefsResponse.data.success) {
          setActiveReferralsCount(activeRefsResponse.data.data.activeCount || 0);
        }

        // Получаем настройки приватности
        const privacyResponse = await api.get('/user/privacy-settings');
        if (privacyResponse.data.success) {
          setShowAsReferrer(privacyResponse.data.data.showAsReferrer);
        }

      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      SoundManager.playButtonClick(); // Звук при копировании

      // Вибрация при копировании
      if ((window as any).Telegram?.WebApp?.HapticFeedback) {
        (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('medium');
      }

      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Ошибка копирования:', err);
    }
  };
  
  const handleShare = () => {
    const shareText = t('referrals.shareText') + referralLink;

    // Открываем ссылку для шаринга в Telegram
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(shareText)}`;
    window.open(shareUrl, '_blank');
  };

  const handleToggleShowAsReferrer = async () => {
    SoundManager.playButtonClick();
    const newValue = !showAsReferrer;
    setLoadingPrivacy(true);

    try {
      const response = await api.post('/user/privacy-settings', {
        showAsReferrer: newValue
      });

      if (response.data.success) {
        setShowAsReferrer(newValue);
        // Вибрация при переключении
        if ((window as any).Telegram?.WebApp?.HapticFeedback) {
          (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('light');
        }
      }
    } catch (error) {
      console.error('Error updating privacy settings:', error);
    } finally {
      setLoadingPrivacy(false);
    }
  };
  
  const handleShareInApp = () => {
    // Шаринг через Web Share API (если доступен) или Telegram shareMessage
    const message = t('referrals.shareMessage') + referralLink;
    const shareData = {
      title: '🌙 TON MOON',
      text: message,
      url: referralLink
    };

    // Пробуем Web Share API (для мобильных устройств)
    if (navigator.share) {
      navigator.share(shareData).catch((err) => {
        console.log('Share cancelled or failed:', err);
      });
    } else if ((window as any).Telegram?.WebApp) {
      // Fallback на Telegram shareMessage
      const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(message)}`;
      (window as any).Telegram.WebApp.openLink(shareUrl);
    } else {
      // Последний fallback
      const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(message)}`;
      window.open(shareUrl, '_blank');
    }
  };

  // Открыть модалку "Как открыть уровень?"
  const handleOpenUnlockModal = (level: number) => {
    SoundManager.playModalOpen();
    if ((window as any).Telegram?.WebApp?.HapticFeedback) {
      (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('light');
    }
    setSelectedLevel(level);
    setShowUnlockModal(true);
  };

  // Загрузка рефералов по уровню
  const fetchLevelReferrals = async (level: number) => {
    try {
      const response = await api.get(`/referrals/by-level/${level}`);
      if (response.data.success) {
        setLevelReferrals(prev => ({
          ...prev,
          [level]: response.data.data.referrals
        }));
      }
    } catch (error) {
      console.error(`Ошибка загрузки рефералов уровня ${level}:`, error);
    }
  };

  // Переключение раскрытия уровня
  const toggleLevel = async (level: number) => {
    SoundManager.playButtonClick();

    // Вибрация
    if ((window as any).Telegram?.WebApp?.HapticFeedback) {
      (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('light');
    }

    const newExpanded = new Set(expandedLevels);
    if (newExpanded.has(level)) {
      newExpanded.delete(level);
    } else {
      newExpanded.add(level);
      // Загружаем рефералов если еще не загружены
      if (!levelReferrals[level]) {
        await fetchLevelReferrals(level);
      }
    }
    setExpandedLevels(newExpanded);
  };

  return (
    <div className="referrals-page">
      <div className="page-content">
        {/* Реферальная ссылка */}
        <div className="referral-link-card">
          <h3 className="card-title">{t('referrals.yourReferralLink')}</h3>
          {/* Пояснение когда есть обе ссылки */}
          {/* Пояснение только если есть обе ссылки */}
          {((!isWebMode && hasEmail) || (isWebMode && user?.telegramId && !String(user.telegramId).startsWith('-'))) && (
            <p style={{fontSize:'11px',color:'rgba(255,255,255,0.4)',textAlign:'center',margin:'0 0 10px',lineHeight:'1.4'}}>
              {t('referrals.sameGame', 'Одна игра — два способа входа. Делитесь любой ссылкой')}
            </p>
          )}
          {/* Метка основной ссылки если есть вторая */}
          {((!isWebMode && hasEmail) || (isWebMode && user?.telegramId && !String(user.telegramId).startsWith('-'))) && (
            <div style={{fontSize:'11px',color: isWebMode ? '#f0b90b' : '#0088cc',fontWeight:600,marginBottom:'4px',letterSpacing:'0.5px'}}>
              {isWebMode ? `🌐 ${t('referrals.webLink', 'САЙТ')}` : '🤖 TELEGRAM'}
            </div>
          )}
          <div className="link-container">
            <input
              type="text"
              value={referralLink}
              readOnly
              className="link-input"
            />
            <button className={`copy-btn ${copySuccess ? 'copied' : ''}`} onClick={handleCopyLink}>
              {copySuccess ? (
                '✅'
              ) : (
                <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="0" width="10" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <rect x="0" y="5" width="10" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="white"/>
                </svg>
              )}
            </button>
          </div>
          {/* Вторая ссылка — сайт (только если email привязан) */}
          {!isWebMode && hasEmail && (
            <>
            <div style={{fontSize:'11px',color:'#f0b90b',fontWeight:600,margin:'10px 0 4px',letterSpacing:'0.5px'}}>🌐 {t('referrals.webLink', 'САЙТ')}</div>
            <div className="link-container">
              <input
                type="text"
                value={webRefLink}
                readOnly
                className="link-input"
                style={{fontSize:'12px'}}
              />
              <button className="copy-btn" onClick={() => {
                navigator.clipboard.writeText(webRefLink);
              }}>
                <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="0" width="10" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <rect x="0" y="5" width="10" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="white"/>
                </svg>
              </button>
            </div>
            </>
          )}
          {/* Вторая ссылка — ТГ бот (если web-mode и аккаунт привязан) */}
          {isWebMode && user?.telegramId && !String(user.telegramId).startsWith('-') && (
            <>
            <div style={{fontSize:'11px',color:'#0088cc',fontWeight:600,margin:'10px 0 4px',letterSpacing:'0.5px'}}>🤖 TELEGRAM</div>
            <div className="link-container">
              <input
                type="text"
                value={tgRefLink}
                readOnly
                className="link-input"
                style={{fontSize:'12px'}}
              />
              <button className="copy-btn" onClick={() => {
                navigator.clipboard.writeText(tgRefLink);
                const el = document.querySelector('.tg-copy-ok') as HTMLElement;
                if (el) { el.style.display = 'inline'; setTimeout(() => el.style.display = 'none', 2000); }
              }}>
                <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="0" width="10" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <rect x="0" y="5" width="10" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="white"/>
                </svg>
              </button>
            </div>
            </>
          )}
          <div className="share-buttons">
            <button className="share-btn" onClick={handleShareInApp}>
              <span className="share-icon">💬</span>
              {t('referrals.shareInApp')}
            </button>
            <button className="share-btn" onClick={handleShare}>
              <span className="share-icon">📤</span>
              {t('referrals.shareInTelegram')}
            </button>
          </div>

          {/* Настройка приватности */}
          <div className="privacy-setting">
            <button
              className={`privacy-toggle ${showAsReferrer ? 'active' : ''}`}
              onClick={handleToggleShowAsReferrer}
              disabled={loadingPrivacy}
            >
              <span className="privacy-toggle-icon">{showAsReferrer ? '👁️' : '🙈'}</span>
              <span className="privacy-toggle-text">
                <span className="privacy-toggle-title">{t('referrals.showAsReferrer')}</span>
                <span className="privacy-toggle-desc">{t('referrals.showAsReferrerDesc')}</span>
              </span>
              <span className={`privacy-toggle-switch ${showAsReferrer ? 'on' : 'off'}`}>
                <span className="switch-knob"></span>
              </span>
            </button>
          </div>
        </div>
        
        {/* Реферальный множитель */}
        {referralStats?.multiplier && (
          <div className={`referral-multiplier-card ${referralStats.multiplier.status}`}>
            <div className="multiplier-header">
              <span className="multiplier-icon">⚡</span>
              <span className="multiplier-title">Реферальный множитель</span>
              <span className="multiplier-value">{Math.round(referralStats.multiplier.value * 100)}%</span>
            </div>
            <div className="multiplier-bar-wrap">
              <div
                className="multiplier-bar-fill"
                style={{ width: `${Math.min(referralStats.multiplier.value * 100, 100)}%` }}
              />
            </div>
            {referralStats.multiplier.status === 'critical' && (
              <div className="multiplier-warning">
                Пополните счёт на ~{formatNumber(referralStats.multiplier.needDeposit, 0)} SPERM и откройте стейк для восстановления множителя
              </div>
            )}
            {referralStats.multiplier.status === 'low' && (
              <div className="multiplier-warning">
                Пополните счёт на ~{formatNumber(referralStats.multiplier.needDeposit, 0)} SPERM и откройте стейк для повышения множителя
              </div>
            )}
            {referralStats.multiplier.status === 'medium' && (
              <div className="multiplier-hint">
                Пополните счёт на ~{formatNumber(referralStats.multiplier.needDeposit, 0)} SPERM и откройте стейк для максимального множителя
              </div>
            )}
            {referralStats.multiplier.status === 'active' && (
              <div className="multiplier-ok">Множитель на максимуме</div>
            )}
          </div>
        )}

        {/* Лидерский статус - упрощенная версия */}
        <div className="leader-status-simple">
          <div className="status-header">
            <h3 className="card-title">
              {t('referrals.leaderStatusTitle')} {leaderStatus.name}
            </h3>
            <button className="info-btn" onClick={() => {
              SoundManager.playModalOpen();
              if ((window as any).Telegram?.WebApp?.HapticFeedback) {
                (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('light');
              }
              setShowLeaderModal(true);
            }}>
              <span>❓</span>
            </button>
          </div>
          {leaderStatus.next && (
            <div className="status-progress">
              <div className="progress-info">
                <span className="progress-current">{activeReferralsCount} {t('referrals.activeWord')} {getReferralWord(activeReferralsCount)} {t('referralLevels.firstLine')}</span>
                <span className="progress-next">{t('referrals.toNext')} {leaderStatus.next - activeReferralsCount}</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${(activeReferralsCount / leaderStatus.next) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
          <div className="status-note">{t('referrals.activeNote')}</div>
        </div>

        {/* График реферальных доходов */}
        <ReferralEarningsChart userId={user?.id} />

        {/* Структура вознаграждений с блокировкой уровней */}
        <div className="rewards-structure">
          <div className="status-header">
            <h3 className="card-title">{t('referralLevels.title')}</h3>
            <button className="info-btn" onClick={() => {
              SoundManager.playModalOpen();
              if ((window as any).Telegram?.WebApp?.HapticFeedback) {
                (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('light');
              }
              setShowRewardsModal(true);
            }}>
              <span>❓</span>
            </button>
          </div>
          <div className="levels-list">
            {[1, 2, 3, 4, 5].map(levelNum => {
              const isUnlocked = isLevelUnlocked(levelNum);
              const req = LEVEL_REQUIREMENTS[levelNum as keyof typeof LEVEL_REQUIREMENTS];
              const levelData = referralStats?.levelsStats?.find((l: any) => l.level === levelNum);
              const referralCount = levelData?.referralCount || 0;
              const totalEarnings = (levelData?.depositBonus || 0) + (levelData?.dailyBonus || 0);

              return (
                <div key={levelNum} className={`level-item ${!isUnlocked ? 'locked' : ''}`}>
                  <div className="level-header">
                    <span className="level-icon">
                      <img src={isUnlocked ? CheckCircleIcon : LockIcon} alt={isUnlocked ? 'Unlocked' : 'Locked'} className="level-icon-svg" />
                    </span>
                    <div className="level-label">
                      <div className="level-title">{t('referralLevels.level')} {levelNum}</div>
                      <div className="level-rewards">
                        {levelNum === 1 ? `5% ${t('referralLevels.onetime')} | 5% ${t('referralLevels.daily')}` :
                         levelNum === 2 ? `3% ${t('referralLevels.onetime')} | 3% ${t('referralLevels.daily')}` :
                         levelNum === 3 ? `2% ${t('referralLevels.onetime')} | 2% ${t('referralLevels.daily')}` :
                         levelNum === 4 ? `1% ${t('referralLevels.onetime')} | 1% ${t('referralLevels.daily')}` :
                         `1% ${t('referralLevels.onetime')} | 1% ${t('referralLevels.daily')}`}
                      </div>
                      {isUnlocked && levelData && (
                        <div className="level-info-row">
                          <div className="level-stat">
                            <img src={UsersIcon} alt="" className="stat-icon-svg" /> {referralCount}
                          </div>
                          {/* Оборот по типам */}
                          {(levelData.spermTurnover > 0 || levelData.usdtTurnover > 0) && (
                            <div className="level-stat level-turnover">
                              {t('referralLevels.turnover')} {levelData.spermTurnover > 0 && <span className="turnover-sperm">{formatNumber(levelData.spermTurnover, 0)} SPERM</span>}
                              {levelData.spermTurnover > 0 && levelData.usdtTurnover > 0 && ' | '}
                              {levelData.usdtTurnover > 0 && <span className="turnover-usdt">{formatNumber(levelData.usdtTurnover, 2)} USDT</span>}
                            </div>
                          )}
                          {/* Заработок по типам */}
                          {(levelData.totalEarnedSperm > 0 || levelData.totalEarnedUsdt > 0) && (
                            <div className="level-stat level-earnings">
                              {t('referralLevels.earned')} {levelData.totalEarnedSperm > 0 && <span className="earned-sperm">{formatNumber(levelData.totalEarnedSperm, 2)} SPERM</span>}
                              {levelData.totalEarnedSperm > 0 && levelData.totalEarnedUsdt > 0 && ' | '}
                              {levelData.totalEarnedUsdt > 0 && <span className="earned-usdt">{formatNumber(levelData.totalEarnedUsdt, 2)} USDT</span>}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <span className="level-status">
                      {isUnlocked ? t('referralLevels.active') : t('referralLevels.locked')}
                    </span>
                  </div>

                  {isUnlocked ? (
                    <>
                      <ReferralTree
                        userId={user?.id}
                        level={levelNum}
                        isOpen={false}
                        maxUnlockedLevel={Math.max(...[1, 2, 3, 4, 5].filter(l => isLevelUnlocked(l)))}
                      />
                    </>
                  ) : (
                    <div className="level-locked-info">
                      <h4>{t('referralLevels.requirements')}</h4>
                      <ul className="requirements-list">
                        <li>• {req.deposit} {t('referralLevels.lunarDeposit')}</li>
                        <li>• {req.referrals} {req.referrals === 1 ? t('referralLevels.activeReferral') : t('referralLevels.activeReferrals')}</li>
                      </ul>

                      <h4>{t('referralLevels.yourProgress')}</h4>
                      <div className="progress-item">
                        <div className="progress-label">
                          {t('referralLevels.deposit')} {userLunarDeposit}/{req.deposit} SPERM ({Math.min(100, Math.round((userLunarDeposit / req.deposit) * 100))}%)
                        </div>
                        <div className="progress-bar-container">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${Math.min(100, (userLunarDeposit / req.deposit) * 100)}%` }}
                          />
                        </div>
                      </div>

                      <div className="progress-item">
                        <div className="progress-label">
                          {t('referralLevels.referrals')} {activeReferralsCount}/{req.referrals} {t('referralLevels.activeLabel')} ({Math.min(100, Math.round((activeReferralsCount / req.referrals) * 100))}%)
                        </div>
                        <div className="progress-bar-container">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${Math.min(100, (activeReferralsCount / req.referrals) * 100)}%` }}
                          />
                        </div>
                      </div>

                      <button className="unlock-help-btn" onClick={() => handleOpenUnlockModal(levelNum)}>
                        ? {t('referralLevels.howToUnlock')} {levelNum}?
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Статистика */}
        <div className="referral-stats">
          <h3 className="card-title">{t('referrals.yourStats')}</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-icon">👥</span>
              <span className="stat-value">{referralStats?.totalStats?.totalReferrals || 0}</span>
              <span className="stat-label">{t('referrals.totalReferrals')}</span>
            </div>
            <div className="stat-card">
              <span className="stat-icon">✅</span>
              <span className="stat-value">{referralStats?.totalStats?.activeReferrals || 0}</span>
              <span className="stat-label">{t('referrals.activeReferrals')}</span>
            </div>
            <div className="stat-card">
              <span className="stat-icon">💰</span>
              <span className="stat-value">{formatNumber(referralStats?.totalStats?.totalEarned || 0, 0)}</span>
              <span className="stat-label">{t('referrals.earnedSPERM')}</span>
            </div>
            <div className="stat-card">
              <span className="stat-icon">📊</span>
              <span className="stat-value">{formatNumber(referralStats?.totalStats?.dailyIncome || 0, 1)}</span>
              <span className="stat-label">{t('referrals.dailyIncome')}</span>
            </div>
            <div className="stat-card">
              <span className="stat-icon">💎</span>
              <span className="stat-value">{formatNumber(referralStats?.totalStats?.totalStaking || 0, 0)}</span>
              <span className="stat-label">{t('referrals.totalInStake')}</span>
            </div>
          </div>
        </div>

        {/* Реферальный множитель — убран отсюда, перенесён выше */}
        
      </div>
      
      {/* Модальное окно с информацией о лидерских статусах */}
      {showLeaderModal && (
        <div className="modal-overlay" onClick={() => { SoundManager.playModalClose(); setShowLeaderModal(false); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => { SoundManager.playModalClose(); setShowLeaderModal(false); }}>✕</button>
            <h2 className="modal-title">{t('referrals.leaderModalTitle')}</h2>

            <div className="modal-description">
              <p>{t('referrals.leaderModalDesc')}</p>
            </div>

            <div className="modal-levels">
              <h3>{t('referrals.leaderModalLevelsTitle')}</h3>

              <div className="modal-level-item">
                <div className="modal-level-header">
                  <span className="modal-level-icon">🥉</span>
                  <span className="modal-level-name">{t('referrals.leaderModalBronze')}</span>
                  <span className="modal-level-bonus">{t('referrals.leaderModalBronzeBonus')}</span>
                </div>
                <div className="modal-level-requirement">{t('referrals.leaderModalBronzeReq')}</div>
              </div>

              <div className="modal-level-item">
                <div className="modal-level-header">
                  <span className="modal-level-icon">🥈</span>
                  <span className="modal-level-name">{t('referrals.leaderModalSilver')}</span>
                  <span className="modal-level-bonus">{t('referrals.leaderModalSilverBonus')}</span>
                </div>
                <div className="modal-level-requirement">{t('referrals.leaderModalSilverReq')}</div>
              </div>

              <div className="modal-level-item">
                <div className="modal-level-header">
                  <span className="modal-level-icon">🥇</span>
                  <span className="modal-level-name">{t('referrals.leaderModalGold')}</span>
                  <span className="modal-level-bonus">{t('referrals.leaderModalGoldBonus')}</span>
                </div>
                <div className="modal-level-requirement">{t('referrals.leaderModalGoldReq')}</div>
              </div>

              <div className="modal-level-item">
                <div className="modal-level-header">
                  <span className="modal-level-icon">🏆</span>
                  <span className="modal-level-name">{t('referrals.leaderModalPlatinum')}</span>
                  <span className="modal-level-bonus">{t('referrals.leaderModalPlatinumBonus')}</span>
                </div>
                <div className="modal-level-requirement">{t('referrals.leaderModalPlatinumReq')}</div>
              </div>

              <div className="modal-level-item">
                <div className="modal-level-header">
                  <span className="modal-level-icon">💎</span>
                  <span className="modal-level-name">{t('referrals.leaderModalDiamond')}</span>
                  <span className="modal-level-bonus">{t('referrals.leaderModalDiamondBonus')}</span>
                </div>
                <div className="modal-level-requirement">{t('referrals.leaderModalDiamondReq')}</div>
              </div>
            </div>

            <div className="modal-benefits">
              <h3>{t('referrals.leaderModalBenefitsTitle')}</h3>
              <ul>
                <li>{t('referrals.leaderModalBenefit1')}</li>
                <li>{t('referrals.leaderModalBenefit2')}</li>
                <li>{t('referrals.leaderModalBenefit3')}</li>
                <li>{t('referrals.leaderModalBenefit4')}</li>
              </ul>
            </div>

            <div className="modal-note">
              {t('referrals.leaderModalNote')}
            </div>

            <div className="modal-current">
              <p>{t('referrals.leaderModalCurrentStatus')} <strong>{leaderStatus.name}</strong></p>
              <p>{t('referrals.leaderModalYourReferrals')} <strong>{activeReferralsCount}</strong> {t('referrals.activeWord')} {t('referralLevels.firstLine')}</p>
              {leaderStatus.next && (
                <p>{t('referrals.leaderModalToNextStatus')} <strong>{leaderStatus.next - activeReferralsCount} {getReferralWord(leaderStatus.next - activeReferralsCount)}</strong></p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Модальное окно с информацией о структуре вознаграждений */}
      {showRewardsModal && (
        <div className="modal-overlay" onClick={() => { SoundManager.playModalClose(); setShowRewardsModal(false); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => { SoundManager.playModalClose(); setShowRewardsModal(false); }}>✕</button>
            <h2 className="modal-title">{t('referrals.rewardsModalTitle')}</h2>

            <div className="modal-description">
              <p>{t('referrals.rewardsModalDesc')}</p>
            </div>

            <div className="modal-reward-types">
              <div className="modal-reward-section">
                <h3>{t('referrals.rewardsModalOneTimeTitle')}</h3>
                <p className="modal-section-desc">{t('referrals.rewardsModalOneTimeDesc')}</p>
                <div className="modal-reward-levels">
                  <div className="modal-reward-level">
                    <span className="level-badge">1</span>
                    <span className="level-text">{t('referrals.rewardsModalLevel1')}</span>
                    <span className="level-value">5%</span>
                  </div>
                  <div className="modal-reward-level">
                    <span className="level-badge">2</span>
                    <span className="level-text">{t('referrals.rewardsModalLevel2')}</span>
                    <span className="level-value">3%</span>
                  </div>
                  <div className="modal-reward-level">
                    <span className="level-badge">3</span>
                    <span className="level-text">{t('referrals.rewardsModalLevel3')}</span>
                    <span className="level-value">2%</span>
                  </div>
                  <div className="modal-reward-level">
                    <span className="level-badge">4</span>
                    <span className="level-text">{t('referrals.rewardsModalLevel4')}</span>
                    <span className="level-value">1%</span>
                  </div>
                  <div className="modal-reward-level">
                    <span className="level-badge">5</span>
                    <span className="level-text">{t('referrals.rewardsModalLevel5')}</span>
                    <span className="level-value">1%</span>
                  </div>
                </div>
              </div>

              <div className="modal-reward-section">
                <h3>{t('referrals.rewardsModalDailyTitle')}</h3>
                <p className="modal-section-desc">{t('referrals.rewardsModalDailyDesc')}</p>
                <div className="modal-reward-levels">
                  <div className="modal-reward-level">
                    <span className="level-badge">1</span>
                    <span className="level-text">{t('referrals.rewardsModalLevel1')}</span>
                    <span className="level-value">5%</span>
                  </div>
                  <div className="modal-reward-level">
                    <span className="level-badge">2</span>
                    <span className="level-text">{t('referrals.rewardsModalLevel2')}</span>
                    <span className="level-value">3%</span>
                  </div>
                  <div className="modal-reward-level">
                    <span className="level-badge">3</span>
                    <span className="level-text">{t('referrals.rewardsModalLevel3')}</span>
                    <span className="level-value">2%</span>
                  </div>
                  <div className="modal-reward-level">
                    <span className="level-badge">4</span>
                    <span className="level-text">{t('referrals.rewardsModalLevel4')}</span>
                    <span className="level-value">1%</span>
                  </div>
                  <div className="modal-reward-level">
                    <span className="level-badge">5</span>
                    <span className="level-text">{t('referrals.rewardsModalLevel5')}</span>
                    <span className="level-value">1%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-example">
              <h3>{t('referrals.rewardsModalExampleTitle')}</h3>
              <p>{t('referrals.rewardsModalExampleDesc')}</p>
              <ul>
                <li><strong>{t('referrals.rewardsModalExampleOneTime')}</strong></li>
                <li><strong>{t('referrals.rewardsModalExampleDaily')}</strong></li>
              </ul>
              <p className="modal-hint">{t('referrals.rewardsModalExampleHint')}</p>
            </div>

            <div className="modal-benefits">
              <h3>{t('referrals.rewardsModalBenefitsTitle')}</h3>
              <ul>
                <li>{t('referrals.rewardsModalBenefit1')}</li>
                <li>{t('referrals.rewardsModalBenefit2')}</li>
                <li>{t('referrals.rewardsModalBenefit3')}</li>
                <li>{t('referrals.rewardsModalBenefit4')}</li>
                <li>{t('referrals.rewardsModalBenefit5')}</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно "Как открыть уровень?" */}
      {showUnlockModal && (
        <div className="modal-overlay" onClick={() => setShowUnlockModal(false)}>
          <div className="modal-content unlock-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowUnlockModal(false)}>✕</button>
            <h2 className="modal-title">{t('referralLevels.unlockTitle')} {selectedLevel}?</h2>

            <div className="modal-description">
              <p>{t('referralLevels.unlockIntro', { level: selectedLevel })}</p>
            </div>

            <div className="unlock-section">
              <h3>{t('referralLevels.yourPart')}</h3>
              <p>{t('referralLevels.createLunar', { amount: LEVEL_REQUIREMENTS[selectedLevel as keyof typeof LEVEL_REQUIREMENTS].deposit })}</p>
              <p className="progress-text">
                {t('referralLevels.youHave')} {userLunarDeposit} SPERM<br />
                {t('referralLevels.needMore')} {Math.max(0, LEVEL_REQUIREMENTS[selectedLevel as keyof typeof LEVEL_REQUIREMENTS].deposit - userLunarDeposit)} SPERM
              </p>
              <button className="action-btn" onClick={() => setShowUnlockModal(false)}>
                {t('referralLevels.createLunarBtn')}
              </button>
            </div>

            <div className="unlock-section">
              <h3>{t('referralLevels.referralsPart')}</h3>
              <p>{t('referralLevels.inviteActive', { count: LEVEL_REQUIREMENTS[selectedLevel as keyof typeof LEVEL_REQUIREMENTS].referrals })}</p>
              <p className="info-text">
                {t('referralLevels.activeHint')}<br />
                {t('referralLevels.starHint')}
              </p>
              <p className="progress-text">
                {t('referralLevels.youHaveActive')} {activeReferralsCount} {t('referralLevels.activeLabel')}<br />
                {t('referralLevels.needLabel')} {LEVEL_REQUIREMENTS[selectedLevel as keyof typeof LEVEL_REQUIREMENTS].referrals}
              </p>
              <button className="action-btn" onClick={handleCopyLink}>
                {t('referralLevels.shareLink')}
              </button>
            </div>

            <div className="unlock-section">
              <h3>{t('referralLevels.whatYouGet')}</h3>
              <p>{t('referralLevels.afterUnlock', { level: selectedLevel })}</p>
              <ul>
                <li>
                  {selectedLevel === 2 ? '3%' :
                   selectedLevel === 3 ? '2%' :
                   selectedLevel === 4 ? '1%' : '1%'} {t('referralLevels.fromDeposit', { level: selectedLevel })}
                </li>
                <li>
                  {selectedLevel === 2 ? '3%' :
                   selectedLevel === 3 ? '2%' :
                   selectedLevel === 4 ? '1%' : '1%'} {t('referralLevels.fromDaily', { level: selectedLevel })}
                </li>
              </ul>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowUnlockModal(false)}>{t('referralLevels.gotIt')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralsPage;